/* RadarToggle — floating pill that opens a large, closeable, full-screen
   modal to configure ORIO Radar layers. Non-invasive on the map when
   closed; when open covers the map with a warm cream sheet.

   Modal contents:
     • Hero image (feat-radar)
     • 3 main toggle groups: Incendi, Meteo & Alluvioni, Salute
     • Sub-toggles inside "Salute" for Leishmaniosi / Filaria / Zecche
     • Bonus: Caldo estremo (map heatmap based on nearby weather zones)
     • Big "Applica e chiudi" CTA at the bottom

   Preferences persist in localStorage via useRadarPrefs.
*/
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Flame, CloudRain, Bug, X, Thermometer, ShieldCheck, Info, BellRing } from "lucide-react";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import { requestNotifPermission } from "@/hooks/useRadarAlertsWatcher";
import { toast } from "sonner";

const LS_KEY = "orio_radar_prefs_v2";

const DEFAULTS = {
  fires: false,
  weather: false,
  disease_leish: false,
  disease_filaria: false,
  disease_ticks: false,
  heat: false,
};

export function useRadarPrefs() {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch {
      // localStorage might be unavailable in private mode — silently ignore
    }
  }, [prefs]);
  return [prefs, setPrefs];
}

/* Adapter that flattens the sub-disease toggles into a single "disease"
   flag consumed by RadarLayer, plus per-type filter. */
export function radarPrefsToLayerConfig(p) {
  return {
    fires: !!p.fires,
    weather: !!p.weather,
    disease: !!(p.disease_leish || p.disease_filaria || p.disease_ticks),
    diseaseFilter: {
      leishmaniosi: !!p.disease_leish,
      filaria: !!p.disease_filaria,
      zecche: !!p.disease_ticks,
    },
    heat: !!p.heat,
  };
}

export default function RadarToggle({ prefs, setPrefs, defaultOpen = false }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const activeCount = Object.values(prefs).filter(Boolean).length;
  const anyOn = activeCount > 0;

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const setAll = (val) => setPrefs({
    fires: val, weather: val,
    disease_leish: val, disease_filaria: val, disease_ticks: val,
    heat: val,
  });

  return (
    <>
      {/* Floating trigger pill */}
      <motion.button
        data-testid="radar-toggle-btn"
        onClick={() => setOpen(true)}
        whileTap={{ scale: 0.94 }}
        className="flex items-center gap-1.5 h-10 rounded-full backdrop-blur bg-white/95 border pl-2.5 pr-3.5 text-[11px] font-semibold text-[#1A1A1A]"
        style={{
          borderColor: anyOn ? "#FF7A1A" : "#F0EBE3",
          boxShadow: anyOn
            ? "0 8px 22px -8px rgba(255,122,26,0.55), 0 2px 6px rgba(0,0,0,0.10)"
            : "0 4px 14px -4px rgba(0,0,0,0.15)",
        }}
      >
        <span className="h-6 w-6 rounded-full flex items-center justify-center"
              style={{ background: anyOn ? "#FF7A1A" : "#F4EFE6", color: anyOn ? "white" : "#7A7A7A" }}>
          <Radar className="h-3.5 w-3.5" />
        </span>
        Radar
        {activeCount > 0 && (
          <span className="ml-0.5 h-4 min-w-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                style={{ background: "#FF7A1A", color: "white" }}>
            {activeCount}
          </span>
        )}
      </motion.button>

      {/* Full-screen configuration modal (portal to <body> to escape any parent stacking context) */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <RadarConfigModal
              prefs={prefs}
              toggle={toggle}
              setAll={setAll}
              onClose={() => setOpen(false)}
              activeCount={activeCount}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

/* ---------- Full-screen modal ---------- */
function RadarConfigModal({ prefs, toggle, setAll, onClose, activeCount }) {
  const [notifState, setNotifState] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const enableNotifs = async () => {
    const res = await requestNotifPermission();
    setNotifState(res);
    if (res === "granted") {
      toast.success("Notifiche attive · ti avvisiamo quando ci sono incendi, caldo estremo o allerte nella tua zona");
    } else if (res === "denied") {
      toast.error("Permesso negato. Puoi riattivarlo dalle impostazioni del browser.");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[1490]"
        style={{ background: "rgba(20,20,22,0.42)", backdropFilter: "blur(4px)" }}
      />
      {/* Sheet */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ type: "spring", damping: 26, stiffness: 240 }}
        className="fixed inset-x-0 top-0 z-[1500] flex flex-col"
        style={{
          background: "#FBF7F1",
          height: "100dvh",
          // Reserve room for the sticky bottom CTA. Modal covers MobileNav
          // (z-1500 > z-400) so no extra offset for nav is needed.
          paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 96px, 96px)",
        }}
        data-testid="radar-config-modal"
      >
        {/* Header pinned */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0"
             style={{ background: "linear-gradient(180deg, #FBF7F1 0%, rgba(251,247,241,0.9) 100%)" }}>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold inline-flex items-center gap-1.5">
              <Radar className="h-3 w-3" /> ORIO Radar
            </div>
            <div className="font-display text-xl leading-tight mt-0.5">
              Configura i layer<br />
              <span className="orio-grad-text">di sicurezza</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="radar-modal-close"
                  className="h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
                  style={{ boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}>
            <X className="h-4 w-4 text-[#1A1A1A]" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pt-2"
             data-testid="radar-modal-scroll">
          {/* Hero illustration */}
          <div className="rounded-3xl overflow-hidden border relative"
               style={{ borderColor: "#F0EBE3", aspectRatio: "5 / 3", background: "#F4EFE6" }}>
            <img
              src={orioIllustration("feat-radar")}
              onError={(e) => { e.currentTarget.src = orioIllustrationFallback("feat-radar"); }}
              alt="ORIO Radar"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          {/* Descrittore SOTTO l'immagine per massima leggibilità */}
          <div className="mt-3 rounded-2xl p-3 border" style={{ background: "white", borderColor: "#F0EBE3" }}>
            <div className="text-[12.5px] text-[#4A4A4A] leading-relaxed">
              Monitora <strong className="text-[#1A1A1A]">incendi</strong>, <strong className="text-[#1A1A1A]">allerte meteo</strong>, <strong className="text-[#1A1A1A]">zone endemiche</strong> e <strong className="text-[#1A1A1A]">caldo estremo</strong> nella tua zona.
              Fonti: EFFIS Copernicus · Protezione Civile · Studi ISS.
            </div>
          </div>

          {/* Notification opt-in — enables the system-level push watcher */}
          <div className="mt-3 rounded-2xl p-3 border flex items-center gap-3"
               style={{
                 background: notifState === "granted" ? "#E8F4EA" : "#FFF6E6",
                 borderColor: notifState === "granted" ? "#B7E1C7" : "#F4D89A",
               }}>
            <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                 style={{
                   background: notifState === "granted" ? "#2DBA60" : "#FF7A1A",
                   color: "white",
                 }}>
              <BellRing className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1A1A1A]">
                {notifState === "granted" ? "Notifiche attive" : "Ricevi notifiche live"}
              </div>
              <div className="text-[10.5px] text-[#7A7A7A] leading-tight mt-0.5">
                {notifState === "granted"
                  ? "Ti avvisiamo per incendi, caldo estremo e allerte a meno di 40 km"
                  : "Avvisi push per incendi, caldo estremo, alluvioni"}
              </div>
            </div>
            {notifState !== "granted" && notifState !== "unsupported" && (
              <button onClick={enableNotifs} data-testid="radar-enable-notifs"
                      className="shrink-0 h-9 px-3 rounded-full text-[12px] font-semibold text-white"
                      style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4813C 100%)" }}>
                Attiva
              </button>
            )}
          </div>

          {/* Quick actions */}
          <div className="mt-4 flex gap-2">
            <button onClick={() => setAll(true)} data-testid="radar-enable-all"
                    className="flex-1 h-10 rounded-full text-[12px] font-semibold text-white"
                    style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4813C 100%)",
                             boxShadow: "0 10px 22px -10px rgba(255,122,26,0.55)" }}>
              Attiva tutti
            </button>
            <button onClick={() => setAll(false)} data-testid="radar-disable-all"
                    className="flex-1 h-10 rounded-full text-[12px] font-semibold border"
                    style={{ background: "white", borderColor: "#F0EBE3", color: "#1A1A1A" }}>
              Disattiva tutti
            </button>
          </div>

          {/* Section: Ambiente */}
          <SectionHeader label="Ambiente" />
          <RadarRow
            testid="radar-fires"
            icon={Flame} iconColor="#E94545" iconBg="#FEE9E9"
            label="Incendi in corso"
            sub="Copernicus EFFIS · hotspot satellitari, aggiornamento continuo"
            on={prefs.fires}
            onToggle={() => toggle("fires")}
          />
          <RadarRow
            testid="radar-weather"
            icon={CloudRain} iconColor="#3B82F6" iconBg="#DDECFA"
            label="Allerte meteo e alluvioni"
            sub="Protezione Civile IT · 187 zone × 4 rischi, aggiornato ogni 4h"
            on={prefs.weather}
            onToggle={() => toggle("weather")}
          />
          <RadarRow
            testid="radar-heat"
            icon={Thermometer} iconColor="#F4B83C" iconBg="#FEF3D9"
            label="Caldo estremo & asfalto"
            sub="Warning temperatura zampe basato su meteo locale"
            on={prefs.heat}
            onToggle={() => toggle("heat")}
          />

          {/* Section: Salute (with granular sub-toggles) */}
          <SectionHeader label="Salute canina" />
          <RadarRow
            testid="radar-leish"
            icon={Bug} iconColor="#B24BF3" iconBg="#F1E5FB"
            label="Leishmaniosi"
            sub="Zone endemiche · costa tirrenica, adriatica, Sicilia, Sardegna"
            on={prefs.disease_leish}
            onToggle={() => toggle("disease_leish")}
          />
          <RadarRow
            testid="radar-filaria"
            icon={Bug} iconColor="#4B8BF3" iconBg="#DDE9FB"
            label="Filaria cardiopolmonare"
            sub="Zone endemiche · Padania, Veneto orientale, costa toscana"
            on={prefs.disease_filaria}
            onToggle={() => toggle("disease_filaria")}
          />
          <RadarRow
            testid="radar-ticks"
            icon={Bug} iconColor="#F4B83C" iconBg="#FEF3D9"
            label="Zecche & TBE"
            sub="Aree boschive Appennino, arco alpino · stagione primavera-autunno"
            on={prefs.disease_ticks}
            onToggle={() => toggle("disease_ticks")}
          />

          {/* Info footer */}
          <div className="mt-5 rounded-2xl p-4 border" style={{ background: "white", borderColor: "#F0EBE3" }}>
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                   style={{ background: "#E8F4EA", color: "#2DBA60" }}>
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[12px] font-semibold text-[#1A1A1A]">Come funziona il Radar</div>
                <div className="text-[11px] text-[#7A7A7A] leading-relaxed mt-1">
                  Ogni layer è indipendente. Attiva solo quello che ti interessa per mantenere la mappa pulita.
                  Tocca una zona sulla mappa per vedere consigli specifici per il tuo cane e la stagione corrente.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 mb-4 rounded-2xl p-3 border-2 border-dashed" style={{ borderColor: "#F0EBE3" }}>
            <div className="flex items-start gap-2 text-[11px] text-[#7A7A7A] leading-relaxed">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#B0A084" }} />
              <span>I dati sono aggregati da fonti pubbliche europee ed italiane. Le zone endemiche sono aggiornate stagionalmente in base a studi ISS e report veterinari.</span>
            </div>
          </div>
        </div>

        {/* Sticky bottom CTA — inside the modal, above content, covers MobileNav */}
        <div className="absolute inset-x-0 z-[1510] px-4 pt-2 pb-3 flex gap-2"
             style={{
               bottom: "max(env(safe-area-inset-bottom, 0px) + 20px, 20px)",
               background: "linear-gradient(180deg, rgba(251,247,241,0) 0%, rgba(251,247,241,0.92) 40%, #FBF7F1 100%)",
             }}>
          <button onClick={onClose} data-testid="radar-apply-close"
                  className="flex-1 h-12 rounded-full text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                           boxShadow: "0 18px 30px -12px rgba(233,69,69,0.55)" }}>
            {activeCount > 0
              ? `Applica ${activeCount} ${activeCount === 1 ? "layer" : "layer"}`
              : "Chiudi"}
          </button>
        </div>
      </motion.div>
    </>
  );
}

function SectionHeader({ label }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#9A9A9A] mt-6 mb-2 px-1">
      {label}
    </div>
  );
}

function RadarRow({ icon: Icon, iconColor, iconBg, label, sub, on, onToggle, testid }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testid}
      className="w-full flex items-center gap-3 p-3 rounded-2xl mb-1.5 text-left transition-all border"
      style={{
        background: on ? "#FFF6E6" : "white",
        borderColor: on ? "#FF7A1A55" : "#F0EBE3",
        boxShadow: on ? "0 6px 16px -8px rgba(255,122,26,0.35)" : "0 2px 6px -3px rgba(0,0,0,0.06)",
      }}
    >
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: iconBg, color: iconColor }}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight">{label}</div>
        <div className="text-[11px] text-[#7A7A7A] leading-tight mt-0.5">{sub}</div>
      </div>
      <div className="shrink-0 h-6 w-11 rounded-full transition-all relative"
           style={{ background: on ? "#FF7A1A" : "#E8E2D6" }}>
        <div className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
             style={{ left: on ? 22 : 2 }} />
      </div>
    </button>
  );
}
